import { useCallback, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import { captureMessage } from '@sentry/react';

import type { Buyer_Leads_Set_Input } from '@graphql/__generated__/graphql';
import {
  BuyerLeadLostReasonDialog,
  type BuyerLeadLostReasonOption,
} from '@modules/_shared/components/buyer-lead/BuyerLeadLostReasonDialog';
import type { OutcomeContext } from '@modules/_shared/components/feed/types';
import {
  GET_ACTIVATION_TARGET,
  GET_LOST_STAGE,
  GET_WON_STAGE,
  UPDATE_BUYER_LEAD,
} from '@modules/_shared/components/feed/utils/buyerLeadQueries';
import { emitFeedRefresh } from '@modules/_shared/utils/feed-refresh';

type BuyerLeadWorkflowAction = 'lost' | 'won' | 'active';

type UseBuyerLeadLostWorkflowArgs = {
  setErrorLike: (error: ErrorLike | null) => void;
};

export const useBuyerLeadLostWorkflow = ({
  setErrorLike,
}: UseBuyerLeadLostWorkflowArgs) => {
  const client = useApolloClient();
  const [lostPrompt, setLostPrompt] = useState<{
    buyerLeadId: string;
    stages: BuyerLeadLostReasonOption[];
  } | null>(null);

  const moveToStage = useCallback(
    async (buyerLeadId: string, stageId: string) => {
      const setFields: Buyer_Leads_Set_Input = { stage_id: stageId };

      try {
        await client.mutate({
          mutation: UPDATE_BUYER_LEAD,
          variables: {
            id: buyerLeadId,
            set: setFields,
          },
        });
      } catch (err) {
        setErrorLike(err as ErrorLike);
        return;
      }

      emitFeedRefresh();
    },
    [client, setErrorLike],
  );

  const executeAction = useCallback(
    async (context: OutcomeContext, action: BuyerLeadWorkflowAction) => {
      const buyerLeadId = context.parentId;

      if (!buyerLeadId) {
        return;
      }

      // A stage lookup failure must not silently fall through and transition
      // the buyer lead to lost/won without the matching stage_id (data
      // integrity regression flagged in PR #23188 review).
      let stageData;
      let currentStageStatus: string | null | undefined;
      let lostStages: BuyerLeadLostReasonOption[] = [];
      try {
        if (action === 'active') {
          const { data } = await client.query({
            query: GET_ACTIVATION_TARGET,
            variables: { id: buyerLeadId },
            fetchPolicy: 'network-only',
          });
          currentStageStatus = data?.buyer_leads_by_pk?.lead_stage?.status;
          stageData = data;
        } else if (action === 'lost') {
          const { data } = await client.query({ query: GET_LOST_STAGE });
          lostStages = (data?.lead_stages ?? []).map(stage => ({
            id: stage.id,
            label: stage.label,
          }));
          stageData = data;
        } else {
          ({ data: stageData } = await client.query({ query: GET_WON_STAGE }));
        }
      } catch (err) {
        setErrorLike(err as ErrorLike);
        return;
      }

      const needsStage =
        action !== 'active' ||
        currentStageStatus === 'won' ||
        currentStageStatus === 'lost';

      if (!needsStage) {
        return;
      }

      if (lostStages.length > 1) {
        setLostPrompt({ buyerLeadId, stages: lostStages });
        return;
      }

      const targetStageId = stageData?.lead_stages?.[0]?.id ?? null;

      if (!targetStageId) {
        const tenantId = await client
          .query({
            query: GET_ACTIVATION_TARGET,
            variables: { id: buyerLeadId },
          })
          .then(({ data }) => data?.buyer_leads_by_pk?.tenant_id)
          .catch(() => undefined);
        captureMessage(`Buyer lead ${action} stage not found`, {
          level: 'error',
          tags: { action, tenant_id: tenantId },
          extra: { buyerLeadId },
        });
        setErrorLike(
          new Error(`Could not find the ${action} stage for this buyer lead.`),
        );
        return;
      }

      await moveToStage(buyerLeadId, targetStageId);
    },
    [client, setErrorLike, moveToStage],
  );

  const triggerLost = useCallback(
    async (context: OutcomeContext) => {
      await executeAction(context, 'lost');
    },
    [executeAction],
  );

  const triggerActive = useCallback(
    async (context: OutcomeContext) => {
      await executeAction(context, 'active');
    },
    [executeAction],
  );

  const triggerWon = useCallback(
    async (context: OutcomeContext) => {
      await executeAction(context, 'won');
    },
    [executeAction],
  );

  const lostReasonDialog = (
    <BuyerLeadLostReasonDialog
      open={lostPrompt != null}
      stages={lostPrompt?.stages ?? []}
      onClose={() => setLostPrompt(null)}
      onConfirm={async stageId => {
        if (lostPrompt == null) {
          return;
        }

        await moveToStage(lostPrompt.buyerLeadId, stageId);
        setLostPrompt(null);
      }}
    />
  );

  return {
    lostReasonDialog,
    triggerActive,
    triggerLost,
    triggerWon,
  };
};
